/**
 * VoiceAIConsentModal Component
 *
 * Modal for obtaining patient consent before starting voice AI transcription
 * during a telehealth session.
 */

import React, { useState } from 'react';
import {
  Mic,
  MicOff,
  AlertCircle,
  CheckCircle,
  XCircle,
  Info,
  Loader2,
} from 'lucide-react';
import {
  TelehealthSession,
  VoiceAIConsentMethod,
  VoiceAIConsentModalProps,
  DEFAULT_CONSENT_SCRIPT,
} from './types';

export default function VoiceAIConsentModal({
  open,
  session,
  consentScript,
  onConsent,
  onClose,
}: VoiceAIConsentModalProps) {
  const [consentMethod, setConsentMethod] = useState<VoiceAIConsentMethod>('verbal');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFullScript, setShowFullScript] = useState(false);

  const scriptText = consentScript || DEFAULT_CONSENT_SCRIPT;

  const handleConsent = async (granted: boolean) => {
    setIsSubmitting(true);
    try {
      await onConsent(granted, consentMethod, notes || undefined);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-teal-50 to-blue-50 dark:from-teal-900/20 dark:to-blue-900/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center">
              <Mic className="h-5 w-5 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Voice AI Transcription Consent
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Request consent before starting call with {session.member_name}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Info banner */}
          <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex gap-3">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-medium mb-1">What is Voice AI Transcription?</p>
                <p>
                  Voice AI creates a real-time transcript of your telehealth conversation,
                  helping you document the visit more accurately while focusing on the member.
                  The transcript can be used to auto-populate clinical notes.
                </p>
              </div>
            </div>
          </div>

          {/* Consent Script */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Read this consent script to the member:
              </label>
              <button
                onClick={() => setShowFullScript(!showFullScript)}
                className="text-xs text-teal-600 hover:text-teal-700 dark:text-teal-400"
              >
                {showFullScript ? 'Show less' : 'Show full script'}
              </button>
            </div>
            <div
              className={`bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap ${
                showFullScript ? '' : 'max-h-48 overflow-hidden relative'
              }`}
            >
              {scriptText}
              {!showFullScript && (
                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-50 dark:from-gray-900 to-transparent" />
              )}
            </div>
          </div>

          {/* Consent Method */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              How was consent obtained?
            </label>
            <div className="flex gap-4">
              {(['verbal', 'written', 'digital'] as VoiceAIConsentMethod[]).map((method) => (
                <label
                  key={method}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                    consentMethod === method
                      ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750'
                  }`}
                >
                  <input
                    type="radio"
                    name="consentMethod"
                    value={method}
                    checked={consentMethod === method}
                    onChange={(e) => setConsentMethod(e.target.value as VoiceAIConsentMethod)}
                    className="sr-only"
                  />
                  <span className="capitalize">{method}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Additional notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional context about the consent..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
              rows={2}
            />
          </div>

          {/* Warning */}
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <div className="flex gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 dark:text-amber-200">
                If the member declines consent, the call will proceed without voice AI transcription.
                You will need to document the encounter manually.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <div className="flex gap-3">
              <button
                onClick={() => handleConsent(false)}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <MicOff className="h-4 w-4" />
                )}
                <span>Consent Denied</span>
              </button>
              <button
                onClick={() => handleConsent(true)}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )}
                <span>Consent Granted</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
